import { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import { useNavigate } from 'react-router-dom';

import {
  namespaces,
  useSharedTranslation,
} from 'us-react-components/dist/hooks/useSharedTranslation';
import { tests } from 'us-react-components/dist/constants/Tests';
import { Modal } from 'us-react-components/dist/Modal/Modal';
import { axios, SitterService } from 'us-web-services';

import { Button, ButtonType } from '../../components';
import { useDependencies } from '../../dependencies';

const MAX_ABOUT = 2500;
const MIN_ABOUT = 150;
const EXAMPLE_PHOTO =
  'https://assets.urbansitter.com/static/img/circle-photos/example-sitter-photo.webp';
const BALLERINAS_BG =
  'https://assets.urbansitter.com/static/img/backgrounds/sitter-ballerinas.webp';

type ModalKey = 'writingPrompts' | 'example' | null;

export const AboutMe = observer((): JSX.Element => {
  const { t } = useSharedTranslation(namespaces.caregiver);
  const navigate = useNavigate();
  const { stores } = useDependencies();
  const sitter = stores.sitter.sitter;

  const [about, setAbout] = useState<string>(sitter?.about ?? '');
  const [aboutValid, setAboutValid] = useState<boolean>(
    () => !sitter?.about || sitter.about.length >= MIN_ABOUT,
  );
  const [submitting, setSubmitting] = useState(false);
  const [openModal, setOpenModal] = useState<ModalKey>(null);

  useEffect(() => {
    document.title = 'UrbanSitter - About You';
  }, []);

  const remaining = MAX_ABOUT - about.length;
  const showError = !aboutValid;
  const canSubmit = !submitting && aboutValid && about.length > 0;

  const submit = async (): Promise<void> => {
    if (!sitter?.id || !canSubmit) return;
    setSubmitting(true);
    try {
      await SitterService.patch(sitter.id, { id: sitter.id, about } as never);
      await stores.sitter.refreshSitter?.();

      const isTester = stores.user.inTestGroup(tests.CAREGIVER_PAYWALL_STRIPE_PAYMENT);
      if (isTester) {
        window.location.href = '/app/caregiver/buy-background-check?nux=true';
      } else {
        navigate('/buy-background-check?nux=true');
      }
    } catch (e) {
      if (axios.isAxiosError(e) && e.response?.data?.errors?.[0]?.message) {
        toast.error(e.response.data.errors[0].message);
      } else {
        toast.error(t('pages.aboutMe.submitFailed'));
      }
      setSubmitting(false);
    }
  };

  const doesPetCare = !!(
    sitter?.willSitDog ||
    sitter?.willSitCat ||
    sitter?.willSitOtherPet
  );

  return (
    <div className='relative overflow-hidden md:min-h-[700px]'>
      <div
        aria-hidden='true'
        className='hidden md:block absolute top-0 left-0 h-full w-[40%] bg-no-repeat bg-left-top bg-contain pointer-events-none'
        style={{ backgroundImage: `url(${BALLERINAS_BG})` }}
      />
      <div className='relative container py-8 max-w-2xl md:mr-0 md:ml-auto md:pr-8 md:pl-0'>
      <h1 className='text-2xl font-semibold mb-4'>{t('pages.aboutMe.heading')}</h1>
      <p className='mb-5 text-sm'>{t('pages.aboutMe.prompt')}</p>
      <p className='mb-6 text-sm'>{t('pages.aboutMe.safetyPrompt')}</p>

      <div className='mb-6'>
        <textarea
          id='sitter-about-box'
          className={`form-control w-full border rounded p-2 focus:ring-sapphire focus:border-sapphire ${
            showError ? 'border-red-500' : 'border-neutral-300'
          }`}
          rows={9}
          maxLength={MAX_ABOUT}
          value={about}
          onChange={(e): void => {
            const value = e.target.value;
            setAbout(value);
            setAboutValid(!!value && value.length >= MIN_ABOUT);
          }}
          placeholder={t('pages.aboutMe.placeholder', { min: MIN_ABOUT })}
        />
        <div className='flex items-start mt-2'>
          <i className='italic text-sm text-neutral-500 flex-1'>
            {t('pages.aboutMe.charactersLeft', { count: remaining })}
          </i>
          {showError && (
            <i className='italic text-sm text-red-600'>
              {t('pages.aboutMe.minLength', { min: MIN_ABOUT })}
            </i>
          )}
        </div>
      </div>

      <div className='text-center mt-16 mb-5'>
        <Button
          id='writing-prompts'
          buttonType={ButtonType.secondary}
          className='mr-3 mb-2'
          onClick={(): void => setOpenModal('writingPrompts')}
        >
          {t('pages.aboutMe.seeWritingIdeas')}
        </Button>
        <Button
          id='see-example'
          buttonType={ButtonType.secondary}
          className='mb-2'
          onClick={(): void => setOpenModal('example')}
        >
          {t('pages.aboutMe.seeExample')}
        </Button>
      </div>

      <div className='text-center mt-5'>
        <Button
          id='submit-location'
          buttonType={ButtonType.primary}
          disabled={!canSubmit}
          onClick={submit}
        >
          {t('pages.aboutMe.continue')}
        </Button>
        <div className='flex justify-center gap-2 mt-4'>
          {[0, 1, 2, 3, 4, 5, 6, 7].map((i) => (
            <span
              key={i}
              className={`w-2 h-2 rounded-full inline-block ${
                i === 6 ? 'bg-sapphire' : 'border border-neutral-300'
              }`}
            />
          ))}
        </div>
      </div>

      <Modal
        isOpen={openModal === 'writingPrompts'}
        onClose={(): void => setOpenModal(null)}
        title={t('pages.aboutMe.writingPrompts.title')}
        className='w-full max-w-lg'
      >
        <div className='space-y-4 text-sm'>
          <p>{t('pages.aboutMe.writingPrompts.heading')}</p>
          <div>
            <h4 className='font-semibold'>
              {t('pages.aboutMe.writingPrompts.generalSection')}
            </h4>
            <ul className='list-disc pl-6 space-y-1'>
              <li>{t('pages.aboutMe.writingPrompts.general1')}</li>
              <li>{t('pages.aboutMe.writingPrompts.general2')}</li>
            </ul>
          </div>
          <div>
            <h4 className='font-semibold'>
              {t('pages.aboutMe.writingPrompts.childCareSection')}
            </h4>
            <ul className='list-disc pl-6 space-y-1'>
              <li>{t('pages.aboutMe.writingPrompts.childCare1')}</li>
              <li>{t('pages.aboutMe.writingPrompts.childCare2')}</li>
              <li>{t('pages.aboutMe.writingPrompts.childCare3')}</li>
              <li>{t('pages.aboutMe.writingPrompts.childCare4')}</li>
            </ul>
          </div>
          {sitter?.willSitSenior && (
            <div>
              <h4 className='font-semibold'>
                {t('pages.aboutMe.writingPrompts.seniorCareSection')}
              </h4>
              <ul className='list-disc pl-6 space-y-1'>
                <li>{t('pages.aboutMe.writingPrompts.seniorCare1')}</li>
                <li>{t('pages.aboutMe.writingPrompts.seniorCare2')}</li>
                <li>{t('pages.aboutMe.writingPrompts.seniorCare3')}</li>
              </ul>
            </div>
          )}
          {doesPetCare && (
            <div>
              <h4 className='font-semibold'>
                {t('pages.aboutMe.writingPrompts.petCareSection')}
              </h4>
              <ul className='list-disc pl-6 space-y-1'>
                <li>{t('pages.aboutMe.writingPrompts.petCare1')}</li>
                <li>{t('pages.aboutMe.writingPrompts.petCare2')}</li>
                <li>{t('pages.aboutMe.writingPrompts.petCare3')}</li>
              </ul>
            </div>
          )}
          {sitter?.willSitHome && (
            <div>
              <h4 className='font-semibold'>
                {t('pages.aboutMe.writingPrompts.householdSection')}
              </h4>
              <ul className='list-disc pl-6 space-y-1'>
                <li>{t('pages.aboutMe.writingPrompts.household1')}</li>
                <li>{t('pages.aboutMe.writingPrompts.household2')}</li>
                <li>{t('pages.aboutMe.writingPrompts.household3')}</li>
              </ul>
            </div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={openModal === 'example'}
        onClose={(): void => setOpenModal(null)}
        title={t('pages.aboutMe.example.title')}
        className='w-full max-w-lg'
      >
        <div className='space-y-3 text-sm'>
          <div className='text-center mb-5'>
            <img
              src={EXAMPLE_PHOTO}
              alt={t('pages.aboutMe.example.name')}
              className='w-20 h-20 rounded-full object-cover mx-auto mb-2'
            />
            <div>{t('pages.aboutMe.example.name')}</div>
          </div>
          <p>{t('pages.aboutMe.example.body1')}</p>
          <p>{t('pages.aboutMe.example.body2')}</p>
          <p>{t('pages.aboutMe.example.body3')}</p>
          <p>{t('pages.aboutMe.example.body4')}</p>
          <p>{t('pages.aboutMe.example.body5')}</p>
        </div>
      </Modal>
      </div>
    </div>
  );
});
